import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import {
  Phone, Mail, MapPin, CheckCircle, Star, Shield, Clock,
  Wrench, Droplets, Send, ChevronRight, ShoppingCart, ArrowRight,
  Users, Award, IndianRupee, Truck, RefreshCw, Settings, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { sanitizeHTML } from '../utils/sanitize';
import {
  trackBooking, trackPhoneClick, trackWhatsAppClick
} from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  Wrench, RefreshCw, Shield, Droplets, Users, Award, Clock, Truck, IndianRupee,
  Star, CheckCircle, MapPin, Phone, Mail, Settings, Heart
};
const Ico = ({ name, ...props }) => {
  const C = ICONS[name] || Star;
  return <C {...props} />;
};

const CityLandingPage = ({ slugProp }) => {
  const { slug: slugParam } = useParams();
  const slug = (slugProp || slugParam || '').toLowerCase();

  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [products, setProducts] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', area: '', serviceType: '', message: '', smsConsent: false
  });

  const loadPage = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/city-pages/${slug}`);
      setPage(res.data);
    } catch {
      setNotFound(true);
    }
  }, [slug]);

  const loadData = useCallback(async () => {
    try {
      const [prodRes, spareRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products`),
        axios.get(`${BACKEND_URL}/api/spare-parts`)
      ]);
      setProducts(Array.isArray(prodRes.data) ? prodRes.data.slice(0, 6) : []);
      setSpareParts(Array.isArray(spareRes.data) ? spareRes.data.slice(0, 6) : []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadPage(); loadData(); }, [loadPage, loadData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.smsConsent) {
      toast.error('Please accept the communication consent');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        serviceType: formData.serviceType,
        ...(formData.email ? { email: formData.email } : {}),
        ...(formData.message ? { message: formData.message } : {}),
        ...(formData.area ? { area: formData.area } : {}),
        city: page?.city_name || slug,
        source: `${slug}_landing_page`
      };
      await axios.post(`${BACKEND_URL}/api/bookings`, payload);
      trackBooking({
        source: `${slug}_landing_page`,
        city: page?.city_name || slug,
        area: formData.area,
        service_type: formData.serviceType,
        value: 350,
      });
      toast.success('Booking Submitted!', {
        description: `Our ${page?.city_name || 'local'} team will call you within 30 minutes.`
      });
      setFormData({ name: '', phone: '', email: '', area: '', serviceType: '', message: '', smsConsent: false });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? (detail[0]?.msg || 'Please check the form fields.')
          : 'Something went wrong. Please call us directly.';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="city-page-not-found">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4 py-16 text-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">City page not found</h1>
            <p className="text-gray-600 mb-6">The city you are looking for is not currently available.</p>
            <Link to="/" className="text-cyan-600 font-semibold hover:underline">Go back to homepage</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="city-page-loading">
        <RefreshCw className="w-8 h-8 text-cyan-600 animate-spin" />
      </div>
    );
  }

  const HERO_IMG = page.hero_image || `${BACKEND_URL}/api/files/rodekho/uploads/general/c15a0d6866e54c718ca2fcfa66fe8736.png`;
  const cityName = page.city_name;
  const canonical = page.canonical_url || `https://www.rodekho.in/city/${slug}`;

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `RO Dekho - ${cityName}`,
    description: page.meta_description,
    url: canonical,
    telephone: page.contact_phone,
    email: page.contact_email,
    address: { '@type': 'PostalAddress', addressLocality: cityName, addressRegion: page.state, addressCountry: 'IN' },
    ...(page.latitude && page.longitude ? { geo: { '@type': 'GeoCoordinates', latitude: page.latitude, longitude: page.longitude } } : {}),
    areaServed: (page.areas || []).map(a => ({ '@type': 'City', name: `${a}, ${cityName}` })),
    openingHoursSpecification: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'], opens: '08:00', closes: '20:00' },
    priceRange: '₹₹',
    image: HERO_IMG
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `RO Water Purifier Service ${cityName}`,
    provider: { '@type': 'Organization', name: 'RO Dekho' },
    areaServed: { '@type': 'City', name: `${cityName}, ${page.state}` },
    serviceType: 'Water Purifier Installation, Repair, and Maintenance',
    description: page.meta_description
  };

  const faqSchema = page.faqs?.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="city-landing-page">
      <Helmet>
        <title>{page.meta_title || `RO Service ${cityName} | RO Dekho`}</title>
        <meta name="description" content={page.meta_description} />
        {page.seo_keywords?.length > 0 && <meta name="keywords" content={page.seo_keywords.join(', ')} />}
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={page.meta_title || `RO Service ${cityName} | RO Dekho`} />
        <meta property="og:description" content={page.meta_description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={page.og_image || HERO_IMG} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`RO Service ${cityName} | RO Dekho`} />
        <meta name="twitter:description" content={page.meta_description} />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-blue-900/80 to-transparent" />
        <div className="relative container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl">
            {page.hero_badge_text && (
              <Badge className="mb-4 bg-cyan-500/20 text-cyan-300 border-cyan-500/40 text-sm px-4 py-1.5" data-testid="hero-badge">
                <MapPin size={14} className="mr-1" /> {page.hero_badge_text}
              </Badge>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
              {page.hero_title_prefix}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{page.hero_title_highlight}</span>
            </h1>
            {page.hero_subtitle && (
              <p className="text-lg md:text-xl text-blue-100 mb-8 leading-relaxed max-w-2xl">{page.hero_subtitle}</p>
            )}
            {page.hero_trust_badges?.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-8">
                {page.hero_trust_badges.map((t, i) => (
                  <span key={i} className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium border border-white/10">
                    <CheckCircle size={14} className="text-cyan-400" /> {t}
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-8 py-7 text-lg rounded-xl shadow-xl shadow-cyan-500/25" data-testid="hero-book-btn">
                Book Free Service Visit
              </Button>
              <a href={`tel:${page.hero_phone}`}
                onClick={() => trackPhoneClick(page.hero_phone, `${slug}_hero`)}
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white font-bold px-8 py-4 text-lg rounded-xl transition-all">
                <Phone size={22} /> {page.hero_phone?.replace('+91', '')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      {page.stats?.length > 0 && (
        <section className="bg-white border-b">
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {page.stats.map((s, i) => (
                <div key={i} className="flex flex-col items-center">
                  <Ico name={s.icon} size={28} className="text-cyan-600 mb-2" />
                  <div className="text-2xl md:text-3xl font-bold text-gray-900">{s.num}</div>
                  <div className="text-sm text-gray-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      {page.services?.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {page.services_title_prefix} <span className="text-cyan-600">{cityName}</span>
              </h2>
              {page.services_subtitle && <p className="text-gray-600 max-w-2xl mx-auto">{page.services_subtitle}</p>}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {page.services.map((svc, i) => (
                <Card key={i} className="border-2 border-gray-100 hover:border-cyan-300 hover:shadow-xl transition-all group cursor-pointer"
                  onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}>
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-cyan-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-cyan-100 transition-colors">
                      <Ico name={svc.icon} size={26} className="text-cyan-600" />
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">{svc.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{svc.desc}</p>
                    {svc.price && <span className="text-cyan-700 font-bold text-sm">{svc.price}</span>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Booking Form */}
      <section id="booking-form" className="py-16 bg-gradient-to-br from-blue-900 via-slate-900 to-cyan-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2 flex flex-col justify-center">
              <Badge className="mb-4 bg-cyan-500/20 text-cyan-300 border-cyan-500/40 w-fit">Book Service</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="form-title">
                {page.form_title || `Book RO Service in ${cityName}`}
              </h2>
              {page.form_subtitle && <p className="text-blue-200 mb-8 leading-relaxed">{page.form_subtitle}</p>}
              <div className="space-y-4">
                {[
                  { icon: Phone, text: page.contact_phone, href: `tel:${page.contact_phone}` },
                  { icon: Mail, text: page.contact_email, href: `mailto:${page.contact_email}` },
                  { icon: MapPin, text: page.contact_address || `${cityName}, ${page.state}`, href: null }
                ].filter(c => c.text).map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <c.icon size={18} className="text-cyan-400" />
                    </div>
                    {c.href ? (
                      <a href={c.href} className="text-blue-100 hover:text-white transition-colors">{c.text}</a>
                    ) : (
                      <span className="text-blue-100">{c.text}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-3">
              <Card className="border-0 shadow-2xl" data-testid="booking-form-card">
                <CardContent className="p-6 md:p-8">
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="city-booking-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name *</label>
                        <Input name="name" required value={formData.name} onChange={handleChange}
                          placeholder="Your full name" className="py-5 border-2 focus:border-cyan-500" data-testid="city-input-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Phone *</label>
                        <Input name="phone" type="tel" required value={formData.phone} onChange={handleChange}
                          placeholder="10-digit phone number" className="py-5 border-2 focus:border-cyan-500" data-testid="city-input-phone" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                      <Input name="email" type="email" value={formData.email} onChange={handleChange}
                        placeholder="Your email (optional)" className="py-5 border-2 focus:border-cyan-500" data-testid="city-input-email" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Area in {cityName} *</label>
                        <select name="area" required value={formData.area} onChange={handleChange}
                          className="w-full px-3 py-3 border-2 border-gray-200 focus:border-cyan-500 rounded-lg bg-white text-sm" data-testid="city-select-area">
                          <option value="">Select your area</option>
                          {(page.areas || []).map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Service Type *</label>
                        <select name="serviceType" required value={formData.serviceType} onChange={handleChange}
                          className="w-full px-3 py-3 border-2 border-gray-200 focus:border-cyan-500 rounded-lg bg-white text-sm" data-testid="city-select-service">
                          <option value="">Select service</option>
                          <option value="installation">RO Installation</option>
                          <option value="repair">RO Repair & Service</option>
                          <option value="amc">AMC Package</option>
                          <option value="filter">Filter Replacement</option>
                          <option value="purchase">Purchase New RO</option>
                          <option value="rental">RO on Rent</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
                      <Textarea name="message" value={formData.message} onChange={handleChange}
                        placeholder="Describe your issue or requirement..." rows={3}
                        className="border-2 focus:border-cyan-500 resize-none" data-testid="city-input-message" />
                    </div>
                    <div className="flex items-start gap-3">
                      <input type="checkbox" id="cityConsent" name="smsConsent" checked={formData.smsConsent}
                        onChange={handleChange}
                        className="w-4 h-4 mt-0.5 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500 flex-shrink-0 cursor-pointer"
                        data-testid="city-consent-checkbox" />
                      <label htmlFor="cityConsent" className="text-xs text-gray-600 leading-relaxed cursor-pointer select-none">
                        I hereby Authorize to send notification on SMS/ messages /email / promotional messages.
                      </label>
                    </div>
                    <Button type="submit" disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-6 text-lg font-semibold rounded-xl shadow-lg"
                      data-testid="city-submit-btn">
                      {isSubmitting ? 'Submitting...' : <><Send size={18} className="mr-2" /> Book Free Service Visit</>}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      {page.show_products_section !== false && products.length > 0 && (
        <section className="py-16 bg-white" data-testid="products-section">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  RO Water Purifiers <span className="text-cyan-600">for {cityName}</span>
                </h2>
                <p className="text-gray-600 mt-2">Best-selling RO systems with free delivery in {cityName}</p>
              </div>
              <Link to="/products" className="hidden md:flex items-center gap-1 text-cyan-600 font-semibold hover:text-cyan-700">
                View All <ChevronRight size={18} />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => (
                <Card key={p.id || p._id} className="border-2 border-gray-100 hover:border-cyan-200 hover:shadow-xl transition-all overflow-hidden group"
                  data-testid={`product-card-${p.slug || p.id}`}>
                  <CardContent className="p-0">
                    <div className="relative overflow-hidden bg-gray-50">
                      <img src={p.images?.[0] || p.image} alt={`${p.name} - RO Purifier ${cityName}`}
                        className="w-full h-52 object-contain p-4 group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      {p.discount > 0 && (
                        <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.discount}% OFF</span>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-gray-900 mb-1 line-clamp-2">{p.name}</h3>
                      <p className="text-sm text-gray-500 mb-3 line-clamp-2">{p.description}</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xl font-bold text-gray-900">₹{(p.salePrice || p.price || 0).toLocaleString()}</span>
                          {p.mrp > (p.salePrice || p.price) && (
                            <span className="text-sm text-gray-400 line-through ml-2">₹{p.mrp.toLocaleString()}</span>
                          )}
                        </div>
                        <Link to={`/product/${p.slug || p.id}`}>
                          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white">
                            <ShoppingCart size={14} className="mr-1" /> View
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-8 text-center md:hidden">
              <Link to="/products">
                <Button variant="outline" className="border-cyan-600 text-cyan-600">View All Products <ArrowRight size={16} className="ml-1" /></Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Spare Parts */}
      {page.show_spareparts_section !== false && spareParts.length > 0 && (
        <section className="py-16 bg-gray-50" data-testid="spares-section">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Genuine <span className="text-cyan-600">Spare Parts</span>
                </h2>
                <p className="text-gray-600 mt-2">Original RO spare parts delivered across {cityName}</p>
              </div>
              <Link to="/spare-parts" className="hidden md:flex items-center gap-1 text-cyan-600 font-semibold hover:text-cyan-700">
                View All <ChevronRight size={18} />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {spareParts.map((sp) => (
                <Link key={sp.id || sp._id} to={`/spare-part/${sp.id || sp._id}`}
                  className="bg-white border-2 border-gray-100 hover:border-cyan-200 rounded-xl p-4 text-center hover:shadow-lg transition-all group">
                  <img src={sp.images?.[0] || sp.image} alt={`${sp.name} - RO spare ${cityName}`}
                    className="w-full h-24 object-contain mb-3 group-hover:scale-105 transition-transform" loading="lazy" />
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">{sp.name}</h3>
                  <span className="text-cyan-700 font-bold text-sm">₹{(sp.salePrice || sp.price || 0).toLocaleString()}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Areas Served */}
      {page.areas?.length > 0 && (
        <section className="py-16 bg-white" data-testid="areas-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Areas We Serve in <span className="text-cyan-600">{cityName}</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Our technicians provide doorstep RO service across all areas of {cityName} district</p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 max-w-5xl mx-auto">
              {page.areas.map((area) => (
                <span key={area}
                  className="px-3 py-1.5 bg-cyan-50 border border-cyan-200 rounded-full text-sm text-cyan-800 hover:bg-cyan-100 transition-colors cursor-default"
                  data-testid={`area-${area.toLowerCase().replace(/\s+/g, '-')}`}>
                  <MapPin size={12} className="inline mr-1 text-cyan-600" />{area}
                </span>
              ))}
            </div>
            {page.seo_keywords?.length > 0 && (
              <div className="sr-only">
                <h3>RO Service Areas in {cityName}</h3>
                {page.seo_keywords.map((kw, i) => <p key={i}>{kw}</p>)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Google Map */}
      {page.map_embed_url && (
        <section className="py-12 bg-white" data-testid="map-section">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center">
                Find Us in <span className="text-cyan-600">{cityName}</span>
              </h2>
              <div className="rounded-2xl overflow-hidden border-2 border-gray-100 shadow-lg">
                <iframe
                  src={page.map_embed_url}
                  width="100%"
                  height="380"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`RO Dekho service area map - ${cityName}`}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Long-form SEO body content */}
      {page.body_content && (
        <section className="py-12 bg-white" data-testid="body-content">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto prose prose-blue prose-headings:text-gray-900 prose-headings:font-bold prose-h2:text-2xl prose-h3:text-xl prose-p:text-gray-700 prose-p:leading-relaxed prose-li:text-gray-700"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(page.body_content) }} />
          </div>
        </section>
      )}

      {/* Nearby Localities */}
      {page.nearby_pages?.length > 0 && (
        <section className="py-12 bg-gray-50" data-testid="nearby-section">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                We Also Serve <span className="text-cyan-600">Nearby Areas</span>
              </h2>
              <p className="text-gray-600 mb-8">Click below to view RO services in your specific area</p>
              <div className="flex flex-wrap justify-center gap-3">
                {page.nearby_pages.map(n => (
                  <Link key={n.slug} to={`/city/${n.slug}`}
                    className="px-5 py-3 bg-white border-2 border-cyan-200 hover:border-cyan-500 hover:bg-cyan-50 rounded-xl text-sm font-semibold text-gray-800 hover:text-cyan-700 transition-all shadow-sm hover:shadow-md inline-flex items-center gap-2"
                    data-testid={`nearby-${n.slug}`}>
                    <MapPin size={14} className="text-cyan-600" />
                    {n.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      {page.why_choose_us?.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
              Why {cityName} Trusts <span className="text-cyan-600">RO Dekho</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {page.why_choose_us.map((item, i) => (
                <Card key={i} className="border-2 border-gray-100 hover:border-cyan-200 transition-all">
                  <CardContent className="p-6">
                    <Ico name={item.icon} size={28} className="text-cyan-600 mb-3" />
                    <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {page.testimonials?.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
              What Our <span className="text-cyan-600">{cityName} Customers</span> Say
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {page.testimonials.map((t, i) => (
                <Card key={i} className="border-2 border-gray-100 hover:border-cyan-200 transition-all">
                  <CardContent className="p-6">
                    <div className="flex gap-0.5 mb-3">
                      {[...Array(t.rating || 5)].map((_, j) => <Star key={j} size={16} className="fill-amber-400 text-amber-400" />)}
                    </div>
                    <p className="text-gray-700 text-sm mb-4 italic leading-relaxed">&ldquo;{t.review}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">{t.name?.[0]}</div>
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{t.name}</div>
                        <div className="text-xs text-gray-500">{t.area}, {cityName}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faqs?.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10">
                Frequently Asked <span className="text-cyan-600">Questions</span>
              </h2>
              <Accordion type="single" collapsible className="space-y-3" data-testid="faq-section">
                {page.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-2 border-gray-200 rounded-xl px-6 bg-white hover:border-cyan-300 transition-colors">
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-cyan-700 py-4">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed pb-4">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 via-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{page.cta_title || `Need RO Service in ${cityName}?`}</h2>
          {page.cta_subtitle && <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">{page.cta_subtitle}</p>}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg" className="bg-white hover:bg-gray-100 text-blue-700 font-bold px-10 py-7 text-lg rounded-xl shadow-xl" data-testid="cta-book-btn">
              Book Free Service
            </Button>
            {page.cta_phone && (
              <a href={`tel:+91${page.cta_phone}`}
                onClick={() => trackPhoneClick(page.cta_phone, `${slug}_cta`)}
                className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-10 py-4 text-lg rounded-xl shadow-xl transition-all">
                <Phone size={22} /> Call: {page.cta_phone}
              </a>
            )}
            {page.cta_whatsapp && (
              <a href={`https://wa.me/91${page.cta_whatsapp}?text=Hi%20RO%20Dekho%2C%20I%20need%20RO%20service%20in%20${cityName}`} target="_blank" rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick(page.cta_whatsapp, `${slug}_cta`)}
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold px-10 py-4 text-lg rounded-xl shadow-xl transition-all">
                WhatsApp Us
              </a>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CityLandingPage;
